import React, { useMemo, useState } from 'react';
import { KpiTile, Panel, Empty, SortTh, sortRows, usePersisted, fmtRub, fmtNum, fmtPct, plural } from './charts';
import MarketingLinks from './MarketingLinks';

// «Маркетинг» (модуль «Источники заказов», 13.10.2026): плитки + «Источники заказов» за период (период —
// выбран наверху). «Ссылки и QR» — карточкой рядом с плитками (владелец 15.10), обратно — «← К отчёту».
// Таблица «Новые клиенты» по месяцу прихода убрана (владелец 15.10: «не нужны»); расчёт остался в
// screens.marketing_clients / GET /analytics/v3/marketing/clients — на случай возврата.
// Отчёт — по источнику, записанному в заказе: места (Инстаграм, карты, канал…) — одной строкой
// на место; свои ссылки — своими строками; без активности у места — прочерки (не скрываем).
// Без модуля — настоящая цифра про магазин и «как подключить» (не прячем вкладку).
// 27.10: «Яндекс Директ» — одна строка, внутри Поиск, РСЯ и каналы Telegram / Max; в канале — ссылки
// отдельных объявлений и «объявления без ссылки» (строки с children раскрываются, открыты сразу).

const dash = (v, f) => (v ? f(v) : '—');
const fmtDM = (iso) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}` : '');
const ORDER_SORTS = {
  name: { get: (r) => r.title },
  people: { get: (r) => r.people || 0 },
  orders: { get: (r) => r.orders || 0 },
  new: { get: (r) => r.new || 0 },
  turnover: { get: (r) => r.turnover || 0 },
  gross: { get: (r) => r.gross || 0 },
  spend: { get: (r) => r.spend || 0 },
  person: { get: (r) => (r.cost_per_person == null ? Infinity : r.cost_per_person) },
  client: { get: (r) => (r.cost_per_client == null ? Infinity : r.cost_per_client) },
  after: { get: (r) => (r.after == null ? -Infinity : r.after) },
};

/** «Ссылки и QR» — карточкой в ряду плиток: где управлять ссылками мест, видно сразу */
function LinksTile({ onLinks }) {
  return (
    <div className="card an-tile">
      <div className="an-tile-top"><span>Ссылки и QR</span></div>
      <div className="an-muted an-tile-sub" style={{ marginTop: 6 }}>Ссылки и QR-коды мест: Инстаграм, карты, визитки, упаковка</div>
      <div className="an-tile-bottom"><button type="button" className="btn btn-secondary btn-sm" onClick={onLinks}>Открыть</button></div>
    </div>
  );
}

function Promo({ data }) {
  const share = data.unknown_share;
  return (
    <section className="card an-panel" style={{ '--ch': '14px' }}>
      <div className="an-panel-head"><h2 className="an-panel-title">Откуда приходят покупатели</h2></div>
      <div className="an-panel-body an-mk-promo">
        {share != null && share >= 30 ? (
          <div className="an-mk-big"><b>{fmtPct(share, 0)}</b><span>заказов за период — источник неизвестен</span></div>
        ) : <div className="an-mk-big"><span>Узнайте, какое место приносит заказы</span></div>}
        <div><b>С модулем «Источники заказов»:</b></div>
        <ul>
          <li>готовые ссылки и QR-коды для Инстаграма, Яндекс Карт, 2ГИС, Telegram-канала, визиток и упаковки — скопировать и вставить;</li>
          <li>свои ссылки для листовок, блогеров и акций — со сроком;</li>
          <li>сколько заказов, денег и новых клиентов дало каждое место — включая заказы в Telegram и Max.</li>
        </ul>
        <div className="an-muted">Подключает разработчик платформы. Напишите: <b>@novaflo_support</b></div>
      </div>
    </section>
  );
}

function Report({ data, blocks, onLinks }) {
  const has = (b) => blocks.includes(b);
  const [open, setOpen] = useState(null);
  const [folded, setFolded] = useState(() => new Set());   // свёрнутые группы (Директ, каналы)
  const toggleGroup = (key) => setFolded(s => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  // По выручке, по убыванию (владелец 15.10); строки без заказов — внизу в порядке мест
  const [sort, setSort] = usePersisted('nf_an_mk_sort2', { key: 'turnover', dir: 'desc' });
  const rows = useMemo(() => sortRows(data.sources || [], sort, ORDER_SORTS), [data.sources, sort]);
  // Колонки цен — всегда (владелец 15.10: «не нашёл стоимости клиента»); без расхода — прочерки
  const anySpend = rows.some(r => r.spend);
  const cols = 9;
  // Итог первой строкой; цены — по местам с расходом (как в плитке «Расход на продвижение»)
  const tot = rows.reduce((a, r) => {
    a.spend += r.spend || 0; a.people += r.people || 0; a.new += r.new || 0; a.orders += r.orders || 0;
    a.turnover += r.turnover || 0; a.gross += r.gross || 0;
    return a;
  }, { spend: 0, people: 0, new: 0, orders: 0, turnover: 0, gross: 0, paidPeople: 0, paidNew: 0 });
  // Цены — по строкам с расходом без вложенных: у «Яндекс Директа» каналы без расхода не разбавляют
  const leaves = (rs) => rs.flatMap(r => (r.children?.length ? leaves(r.children) : [r]));
  leaves(rows).forEach(r => {
    if (r.spend && !r.blind && r.kind !== 'other') { tot.paidPeople += r.people || 0; tot.paidNew += r.new || 0; }
  });

  const renderRow = (r, depth) => {
    const kids = r.children || [];
    const group = kids.length > 0;
    const isOpen = group ? !folded.has(r.key) : open === r.key;
    const toggle = () => (group ? toggleGroup(r.key) : setOpen(o => (o === r.key ? null : r.key)));
    const grey = r.kind === 'staff' || r.kind === 'unknown' || !r.active;
    // Строка без ссылки (только бюджет), объявления без ссылки и «Прочее продвижение» — заказов нет, только расход
    const noLink = r.kind === 'link' && r.link_enabled === false;
    const noLinkAds = r.kind === 'nolink';
    const other = r.kind === 'other';
    const sub = [r.period ? `${r.period}${r.ended ? ' · закончилась' : ''}` : '', noLink ? 'без ссылки' : ''].filter(Boolean).join(' · ');
    // Расход, часть которого — план (месяц не подтверждён в «Затратах»)
    const spendPlan = r.spend_plan > 0 ? (r.spend_plan >= r.spend - 1 ? 'план' : 'часть — план') : '';
    return (
      <React.Fragment key={r.key}>
        <tr className={`${grey ? 'an-row-muted' : ''}${!group && isOpen ? ' sel' : ''}${depth ? ' an-sub-row' : ''}`} tabIndex={0}
          onClick={toggle} onKeyDown={(e) => { if (e.key === 'Enter') toggle(); }}>
          <td style={depth ? { paddingLeft: 10 + depth * 20 } : undefined}>
            <span className="an-caret" aria-hidden="true">{isOpen ? '▾' : '▸'}</span>{depth ? r.title : <b>{r.title}</b>}
            {sub && <div className="an-muted an-small">{sub}</div>}</td>
          <td className="r an-m-hide">{dash(r.spend, fmtRub)}{spendPlan && <div className="an-muted an-small">{spendPlan}</div>}</td>
          <td className="r an-m-hide">{dash(r.people, fmtNum)}</td>
          <td className="r an-m-hide">{dash(r.cost_per_person, fmtRub)}</td>
          <td className="r">{noLinkAds ? '—' : dash(r.new, fmtNum)}</td>
          <td className="r an-b">{dash(r.cost_per_client, fmtRub)}</td>
          <td className="r an-m-hide">{noLinkAds ? '—' : dash(r.orders, fmtNum)}</td>
          <td className="r an-m-hide">{dash(r.turnover, fmtRub)}</td>
          {(() => { const v = r.after != null ? r.after : (r.blind ? null : r.gross);
            return <td className={`r${v < 0 ? ' an-bad' : ''}`}>{v ? fmtRub(v) : '—'}</td>; })()}
        </tr>
        {group && isOpen && kids.map(c => renderRow(c, depth + 1))}
        {!group && isOpen && (
          <tr className="an-expand"><td colSpan={cols}>
            <div className="an-small">
              {noLink && (<>
                <div className="an-muted">Ссылки нет — заказы к строке не привязываются.</div>
                <button type="button" className="an-link" onClick={onLinks}>Выдать ссылку →</button>
              </>)}
              {noLinkAds && (
                <div className="an-muted">В этих объявлениях нет нашей ссылки — кто пришёл и купил, не видно, поэтому здесь только расход и клики.</div>
              )}
              {other && (<>
                <div className="an-muted">Общий маркетинг — внесён в «Финансах»: то, что не привязать к ссылке. Заказов у этой строки не видно, поэтому без «После расхода».</div>
              </>)}
              {!noLink && !noLinkAds && !other && (<>
                {r.entries ? <div>Пришли: на сайт {fmtNum(r.entries.site)} · в Telegram {fmtNum(r.entries.tg)} · в Max {fmtNum(r.entries.max)}</div>
                  : r.people_from !== 'clicks' && <div className="an-muted">Переходов по ссылкам за период не было</div>}
                {r.orders > 0 && <div>Заказали: с сайта {fmtNum(r.by.web)} · в Telegram {fmtNum(r.by.telegram)} · в Max {fmtNum(r.by.max)}</div>}
                {r.people_from === 'clicks' && <div className="an-muted">Переходы — клики из кабинета Директа.</div>}
                {r.fee > 0 && <div className="an-muted">Комиссия площадки {fmtRub(r.fee)} — уже вычтена из прибыли, поэтому не в «Расходе».</div>}
                {r.people > 0 && r.new > 0 && <div>Из переходов стали новыми клиентами: {fmtNum(r.new)} из {fmtNum(r.people)} ({fmtPct(r.new / r.people * 100, 1)})</div>}
                {r.cost_per_order != null && <div>Цена заказа: {fmtRub(r.cost_per_order)}</div>}
                {r.after != null && <div>Валовая прибыль {fmtRub(r.gross)} − расход {fmtRub(r.spend)} = <b>{fmtRub(r.after)}</b></div>}
              </>)}
              {r.clicks > 0 && r.people_from !== 'clicks' && <div>Кликов в Директе: {fmtNum(r.clicks)}</div>}
              {r.ads?.length > 0 && (
                <div>Объявления: {r.ads.map(a => `с ${fmtDM(a.from)} по ${fmtDM(a.to)} — ${fmtRub(a.cost)}, ${plural(a.clicks, 'клик', 'клика', 'кликов')}`).join('; ')}</div>
              )}
              {(r.kind === 'place' || r.kind === 'link' || r.kind === 'direct' || noLinkAds) && !noLink && (
                <button type="button" className="an-link" onClick={onLinks}>
                  {r.kind === 'direct' ? 'Метки и расход Директа →' : noLinkAds ? 'Указать ссылки объявлений →' : 'Ссылки и расход этого места →'}</button>
              )}
              {r.kind === 'unknown' && <div className="an-muted">Заказы без нашей ссылки и без рекламы: сарафан, постоянные покупатели, прямой заход в бота.</div>}
            </div>
          </td></tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="an-stack">
      {/* «Источник известен» и плашка ниже говорят одно и то же — при плашке плитку не показываем */}
      {(() => {
        const tiles = has('marketing_tiles') ? (data.tiles || []).filter(t => !(t.key === 'known' && data.unknown_share >= 30)) : [];
        const n = tiles.length + (onLinks ? 1 : 0);
        return n > 0 && (
          <div className="an-kpis" data-n={n}>
            {tiles.map(t => <KpiTile key={t.key} tile={{ ...t, hero: false }} compare={false} />)}
            {onLinks && <LinksTile onLinks={onLinks} />}
          </div>
        );
      })()}
      {data.unknown_share != null && data.unknown_share >= 30 && (
        <div className="card an-callout">
          <b>У {fmtPct(data.unknown_share, data.unknown_share > 99 ? 1 : 0)} заказов источник неизвестен.</b>{' '}
          Расставьте ссылки по местам — будет видно, откуда приходят покупатели.{' '}
          <button type="button" className="an-link" onClick={onLinks}>Открыть ссылки</button>
        </div>
      )}
      {has('sources') && (
        <Panel title="Источники заказов">
          <div className="an-muted an-small" style={{ marginBottom: 10 }}>Сколько стоит человек, перешедший по ссылке, и сколько — новый клиент, по каждому месту. Цена клиента сравнивается с тем, что новый клиент приносит.
            {!anySpend && <> Расходов за период нет — цены пустые. {onLinks && <button type="button" className="an-link" onClick={onLinks}>Внести расход места →</button>}</>}</div>
          {rows.length ? (
            <div className="an-scroll-x">
              <table className="an-table an-table-click an-src-table">
                <thead><tr>
                  <SortTh k="name" sort={sort} setSort={setSort} className="">Источник</SortTh>
                  <SortTh k="spend" sort={sort} setSort={setSort} className="r an-m-hide" hint="Внесённый расход места и Директ — за выбранный период">Расход</SortTh>
                  <SortTh k="people" sort={sort} setSort={setSort} className="r an-m-hide" hint="Люди, перешедшие по ссылкам места за период">Переходы</SortTh>
                  <SortTh k="person" sort={sort} setSort={setSort} className="r an-m-hide" hint="Расход ÷ переходы: сколько стоил один человек, перешедший по ссылке">Цена перехода</SortTh>
                  <SortTh k="new" sort={sort} setSort={setSort} hint="Первая покупка в жизни — в этом периоде. Клиент относится к месту, откуда впервые пришёл по ссылке (если купил в течение 6 месяцев), иначе — к месту заказа">Новые клиенты</SortTh>
                  <SortTh k="client" sort={sort} setSort={setSort} hint="Расход ÷ новые клиенты: сколько стоил один новый покупатель. Сравнивать с плиткой «Новый клиент приносит»">Цена клиента</SortTh>
                  <SortTh k="orders" sort={sort} setSort={setSort} className="r an-m-hide" hint="Все заказы периода из этого места — и новых, и прежних покупателей (переход не раньше 30 дней до заказа)">Заказы</SortTh>
                  <SortTh k="turnover" sort={sort} setSort={setSort} className="r an-m-hide" hint="Что заплатили покупатели по заказам из этого места">Выручка</SortTh>
                  <SortTh k="after" sort={sort} setSort={setSort} hint="Валовая прибыль заказов места минус расход; без расхода — валовая прибыль">После расхода</SortTh>
                </tr></thead>
                <tbody>
                  <tr className="an-total-row">
                    <td>Всего</td>
                    <td className="r an-m-hide">{dash(tot.spend, fmtRub)}</td>
                    <td className="r an-m-hide">{dash(tot.people, fmtNum)}</td>
                    <td className="r an-m-hide">{tot.spend && tot.paidPeople ? fmtRub(tot.spend / tot.paidPeople) : '—'}</td>
                    <td className="r">{dash(tot.new, fmtNum)}</td>
                    <td className="r">{tot.spend && tot.paidNew ? fmtRub(tot.spend / tot.paidNew) : '—'}</td>
                    <td className="r an-m-hide">{dash(tot.orders, fmtNum)}</td>
                    <td className="r an-m-hide">{dash(tot.turnover, fmtRub)}</td>
                    <td className={`r${tot.gross - tot.spend < 0 ? ' an-bad' : ''}`}>{dash(tot.gross - tot.spend, fmtRub)}</td>
                  </tr>
                  {rows.map(r => renderRow(r, 0))}
                </tbody>
              </table>
            </div>
          ) : <Empty />}
          <div className="an-muted an-small" style={{ marginTop: 8 }}>
            Нажмите на строку — подробности. Новый клиент — к месту, откуда впервые пришёл по ссылке (если купил в течение 6 месяцев);
            заказ — к месту последнего перехода за 30 дней. Расход — Директ и внесённый вручную, доля за выбранный период; пока месяц
            не подтверждён в «Финансы → Затраты» — по плану. Комиссия площадок — не расход: она уже вычтена из прибыли.
          </div>
        </Panel>
      )}
    </div>
  );
}

export default function MarketingTab({ data, blocks, view, onView, showToast, onGoFinance }) {
  const has = (b) => blocks.includes(b);
  if (has('module_promo')) return <Promo data={data} />;
  if (view === 'links' && has('link_builder')) {
    return (
      <div className="an-stack">
        <div><button type="button" className="an-link" onClick={() => onView('report')}>← К отчёту</button></div>
        <MarketingLinks showToast={showToast} onGoFinance={onGoFinance} />
      </div>
    );
  }
  return <Report data={data} blocks={blocks} onLinks={has('link_builder') ? () => onView('links') : null} />;
}
